(() => {
  "use strict";

  const config = window.JazzyTheme || {};
  const body = document.body;
  const menuButton = document.querySelector(".menu-toggle");
  const navigation = document.querySelector(".site-nav");
  const cartButton = document.querySelector("[data-cart-toggle]");
  const cartPanel = document.querySelector("[data-cart-drawer]");
  const cartClose = document.querySelector("[data-cart-close]");
  const cartScrim = document.querySelector("[data-cart-scrim]");
  const cartCount = document.querySelector(".cart-count");
  const cartItems = document.querySelector("[data-cart-items]");
  const cartFooter = document.querySelector("[data-cart-footer]");
  const cartTotal = document.querySelector("[data-cart-total]");
  const cartStatus = document.querySelector("[data-cart-status]");
  const rootUrl = config.rootUrl || "/";

  const route = (path) => rootUrl.endsWith("/") ? `${rootUrl}${path}` : `${rootUrl}/${path}`;

  const escapeHTML = (value = "") => String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

  const formatWithDelimiters = (number, precision = 2, thousands = ",", decimal = ".") => {
    const value = (Number(number) / 100).toFixed(precision);
    const parts = value.split(".");
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
  };

  const formatMoney = (cents) => {
    const format = config.moneyFormat || "${{amount}}";
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return `$${formatWithDelimiters(cents)}`;

    let value;
    switch (match[1]) {
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      default:
        value = formatWithDelimiters(cents);
    }
    return format.replace(match[0], value);
  };

  const announce = (message) => {
    if (cartStatus) cartStatus.textContent = message;
  };

  const setMenu = (open) => {
    if (!menuButton || !navigation) return;
    menuButton.setAttribute("aria-expanded", String(open));
    navigation.classList.toggle("open", open);
    body.classList.toggle("menu-open", open);
    const label = menuButton.querySelector(".sr-only");
    if (label) label.textContent = open ? "Close navigation" : "Open navigation";
  };

  const setCart = (open) => {
    if (!cartPanel || !cartButton || !cartScrim) return;
    cartPanel.classList.toggle("open", open);
    cartPanel.setAttribute("aria-hidden", String(!open));
    cartButton.setAttribute("aria-expanded", String(open));
    cartScrim.hidden = !open;
    body.classList.toggle("cart-open", open);
    requestAnimationFrame(() => cartScrim.classList.toggle("visible", open));
    if (open) cartClose?.focus();
  };

  const updateCartCount = (count) => {
    if (!cartCount || !cartButton) return;
    cartCount.textContent = String(count);
    cartCount.classList.toggle("has-items", count > 0);
    cartButton.setAttribute("aria-label", `Open cart, ${count} ${count === 1 ? "item" : "items"}`);
  };

  const emptyCartMarkup = () => `
    <div class="empty-cart">
      <span class="empty-cart-sun" aria-hidden="true"></span>
      <p>Your bag is soaking up the sun.</p>
      <a class="text-link" href="${escapeHTML(config.allProductsUrl || route("collections/all"))}">Find something jazzy</a>
    </div>`;

  const lineItemMarkup = (item) => {
    const image = item.image
      ? `<a href="${escapeHTML(item.url)}" tabindex="-1"><img src="${escapeHTML(item.image)}" alt="" width="72" height="65" loading="lazy"></a>`
      : `<span class="cart-image-placeholder" aria-hidden="true"></span>`;
    const variant = item.variant_title && item.variant_title !== "Default Title"
      ? `<p class="cart-variant">${escapeHTML(item.variant_title)}</p>`
      : "";

    return `
      <article class="cart-line" data-cart-key="${escapeHTML(item.key)}">
        ${image}
        <div>
          <h3><a href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a></h3>
          ${variant}
          <p>${formatMoney(item.final_line_price)}</p>
          <div class="cart-quantity" aria-label="Quantity for ${escapeHTML(item.product_title)}">
            <button type="button" data-cart-quantity="${Math.max(0, item.quantity - 1)}" data-cart-key="${escapeHTML(item.key)}" aria-label="Decrease quantity">&minus;</button>
            <span>${item.quantity}</span>
            <button type="button" data-cart-quantity="${item.quantity + 1}" data-cart-key="${escapeHTML(item.key)}" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <button class="remove-item" type="button" data-cart-remove data-cart-key="${escapeHTML(item.key)}" aria-label="Remove ${escapeHTML(item.product_title)}">&times;</button>
      </article>`;
  };

  const renderCart = (cart) => {
    if (!cartItems || !cartFooter || !cartTotal) return;
    updateCartCount(cart.item_count);
    if (!cart.item_count) {
      cartItems.innerHTML = emptyCartMarkup();
      cartFooter.hidden = true;
      return;
    }

    cartItems.innerHTML = cart.items.map(lineItemMarkup).join("");
    cartTotal.textContent = formatMoney(cart.total_price);
    cartFooter.hidden = false;
  };

  const parseResponse = async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || "Something went wrong. Please try again.");
    return data;
  };

  const getCart = async () => {
    const response = await fetch(route("cart.js"), {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    return parseResponse(response);
  };

  const refreshCart = async () => {
    const cart = await getCart();
    renderCart(cart);
    return cart;
  };

  const changeCartLine = async (key, quantity) => {
    cartPanel?.setAttribute("aria-busy", "true");
    try {
      const response = await fetch(route("cart/change.js"), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({ id: key, quantity }),
      });
      const cart = await parseResponse(response);
      renderCart(cart);
      announce(quantity === 0 ? "Item removed from your bag." : "Bag quantity updated.");
    } catch (error) {
      announce(error.message);
    } finally {
      cartPanel?.removeAttribute("aria-busy");
    }
  };

  const addVariant = async (id, quantity = 1) => {
    const response = await fetch(route("cart/add.js"), {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
      body: JSON.stringify({ items: [{ id: Number(id), quantity: Number(quantity) || 1 }] }),
    });
    await parseResponse(response);
    return refreshCart();
  };

  menuButton?.addEventListener("click", () => {
    setMenu(menuButton.getAttribute("aria-expanded") !== "true");
  });

  navigation?.addEventListener("click", (event) => {
    if (event.target.closest("a")) setMenu(false);
  });

  cartButton?.addEventListener("click", async () => {
    const opening = cartButton.getAttribute("aria-expanded") !== "true";
    if (opening) {
      try { await refreshCart(); } catch (error) { announce(error.message); }
    }
    setCart(opening);
  });
  cartClose?.addEventListener("click", () => setCart(false));
  cartScrim?.addEventListener("click", () => setCart(false));

  cartItems?.addEventListener("click", (event) => {
    const removeButton = event.target.closest("[data-cart-remove]");
    const quantityButton = event.target.closest("[data-cart-quantity]");
    const button = removeButton || quantityButton;
    if (!button) return;
    const quantity = removeButton ? 0 : Number(quantityButton.dataset.cartQuantity);
    changeCartLine(button.dataset.cartKey, quantity);
  });

  document.addEventListener("click", async (event) => {
    const button = event.target.closest("[data-quick-add]");
    if (!button || button.disabled) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = "Adding...";
    try {
      await addVariant(button.dataset.variantId, 1);
      button.textContent = "Added!";
      announce(`${button.dataset.product || "Item"} added to your bag.`);
      setCart(true);
    } catch (error) {
      button.textContent = "Try again";
      announce(error.message);
    } finally {
      window.setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 1100);
    }
  });

  document.querySelectorAll("[data-product-form]").forEach((form) => {
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const submit = form.querySelector('[type="submit"]');
      const status = form.querySelector(".product-form-status");
      const data = new FormData(form);
      const originalText = submit?.textContent;
      if (submit) {
        submit.disabled = true;
        submit.textContent = "Adding...";
      }
      try {
        await addVariant(data.get("id"), data.get("quantity"));
        if (status) status.textContent = "Added to your good vibes bag!";
        setCart(true);
      } catch (error) {
        if (status) status.textContent = error.message;
      } finally {
        if (submit) {
          submit.disabled = false;
          submit.textContent = originalText;
        }
      }
    });
  });

  // Storefront filtering is done by Shopify now (collection.filters), so the only
  // job left here is opening the drawer and submitting the form.
  const filtersForm = document.getElementById("CollectionFiltersForm");
  const filtersToggle = document.querySelector("[data-filters-toggle]");
  const filtersDrawer = document.querySelector("[data-filters-drawer]");
  const filtersClose = document.querySelector("[data-filters-close]");
  const filtersScrim = document.querySelector("[data-filters-scrim]");

  const setFilters = (open) => {
    if (!filtersDrawer || !filtersToggle || !filtersScrim) return;
    filtersDrawer.classList.toggle("open", open);
    filtersDrawer.setAttribute("aria-hidden", String(!open));
    filtersToggle.setAttribute("aria-expanded", String(open));
    filtersScrim.hidden = !open;
    body.classList.toggle("filters-open", open);
    requestAnimationFrame(() => filtersScrim.classList.toggle("visible", open));
    if (open) filtersClose?.focus();
  };

  filtersToggle?.addEventListener("click", () => setFilters(true));
  filtersClose?.addEventListener("click", () => setFilters(false));
  filtersScrim?.addEventListener("click", () => setFilters(false));

  if (filtersForm) {
    // Sort applies immediately; checkboxes and price wait for Apply so you can
    // pick several without a reload between each one.
    // Empty fields would serialise as "filter.v.price.gte=" and clutter the URL.
    filtersForm.addEventListener("submit", () => {
      filtersForm.querySelectorAll("input, select").forEach((field) => {
        if (field.type !== "checkbox" && field.value === "") field.disabled = true;
      });
    });

    filtersForm.querySelector("[data-auto-submit]")?.addEventListener("change", () => {
      filtersForm.requestSubmit();
    });

    const activeCount = filtersForm.querySelector("[data-active-count]");
    if (activeCount) {
      const checked = filtersForm.querySelectorAll('input[type="checkbox"]:checked').length;
      const priced = [...filtersForm.querySelectorAll('input[type="number"]')].filter((i) => i.value !== "").length;
      const total = checked + (priced > 0 ? 1 : 0);
      if (total > 0) activeCount.textContent = ` (${total})`;
    }
  }

  const showDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.showModal === "function") dialog.showModal();
    else dialog.setAttribute("open", "");
  };

  document.querySelectorAll(".wholesale-open").forEach((opener) => {
    opener.addEventListener("click", (event) => {
      const dialog = document.querySelector(opener.getAttribute("href"));
      if (!dialog) return;
      event.preventDefault();
      showDialog(dialog);
    });
  });

  document.querySelectorAll("[data-wholesale-dialog]").forEach((dialog) => {
    if (dialog.querySelector(".form-success, .form-errors")) showDialog(dialog);
  });

  const galleryDialog = document.querySelector("[data-gallery-dialog]");
  const galleryImage = galleryDialog?.querySelector("img");
  const galleryCaption = galleryDialog?.querySelector("p");
  document.querySelectorAll(".mural-tile").forEach((tile) => {
    tile.addEventListener("click", () => {
      if (!galleryDialog || !galleryImage || !galleryCaption) return;
      galleryImage.src = tile.dataset.galleryImage;
      galleryImage.removeAttribute("srcset");
      galleryImage.alt = tile.dataset.galleryAlt;
      galleryCaption.textContent = tile.dataset.galleryAlt;
      showDialog(galleryDialog);
    });
  });

  document.querySelectorAll("dialog .dialog-close").forEach((button) => {
    button.addEventListener("click", () => button.closest("dialog")?.close());
  });

  document.querySelectorAll("dialog").forEach((dialog) => {
    dialog.addEventListener("click", (event) => {
      const bounds = dialog.getBoundingClientRect();
      const inside = event.clientX >= bounds.left && event.clientX <= bounds.right
        && event.clientY >= bounds.top && event.clientY <= bounds.bottom;
      if (!inside) dialog.close();
    });
  });

  document.querySelectorAll("[data-product-media]").forEach((button) => {
    button.addEventListener("click", () => {
      const image = document.querySelector(".product-featured-media img");
      if (!image) return;
      image.src = button.dataset.productMedia;
      image.removeAttribute("srcset");
      image.alt = button.dataset.productMediaAlt || "";
    });
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    setMenu(false);
    if (cartPanel?.classList.contains("open")) setCart(false);
    if (filtersDrawer?.classList.contains("open")) setFilters(false);
  });

  window.addEventListener("resize", () => {
    if (window.innerWidth > 800) setMenu(false);
  });
})();






